import { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { CellValueTheme } from '@app/shared/components/cell-value/cell-value-theme';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { Pwerm2WaterfallPartialTableProps } from '../pwerm-waterfall-utils/pwerm2-waterfall-shared';
import { enumKeyByValue } from '@app/shared/helpers';
import {
  WaterfallSectionsErf,
  WaterfallSections,
} from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { WaterfallTableExpandableRow } from '@app/modules/projects/outputs/pwerm2-summary-waterfall/waterfall-table/WaterfallTableExpandableRow';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import TableSectionValuesPerMultiple from '../../components/TableSectionValuesPerMultiple';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseValue,
  getWaterfallCaseYearMultipleValue,
  getWaterfallCaseYearValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { getPwermValuationValueDriver } from '@app/shared/models/helpers/project-helpers';
import useTogglerPwermWaterfall from '@app/core/hooks/useTogglerPwermWaterfall';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';

export const Pwerm2WaterfallEnterpriseValuePartialTable: FC<Pwerm2WaterfallPartialTableProps> = ({
  columnsCount,
}): JSX.Element => {
  const project = useAppSelector((state) => state.project.projectDraft);
  const { l } = useLocale();
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const valuationValueDriver = getPwermValuationValueDriver(project);
  const pwermWaterfallTableStates = useAppSelector(
    (state) => state.uiValues.pwermWaterfallTableExpandStates
  );
  const { handleToggle } = useTogglerPwermWaterfall(WaterfallSectionsErf.EnterpriseValueEV);

  return (
    <>
      <tbody data-testid="enterprise-value-expandable-row">
        <WaterfallTableExpandableRow
          title={l('_EnterpriseValueEV')}
          id={enumKeyByValue(WaterfallSections, WaterfallSections.EnterpriseValueEV)}
          isOpen={pwermWaterfallTableStates[WaterfallSectionsErf.EnterpriseValueEV]}
          setIsOpen={handleToggle}
          colCount={columnsCount}
        />
      </tbody>
      <tbody
        className={classNames('table-primary__table-section', {
          'table-primary__table-section--collapsed':
            !pwermWaterfallTableStates[WaterfallSectionsErf.EnterpriseValueEV],
        })}
        data-testid="enterprise-value-section">
        <TableSectionValuesPerForecast
          renderSpacer={false}
          title={valuationValueDriver.narrative}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.valueDriver
              )}
              dataTestId={`value-driver-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
          renderAdditionalCell={({ caseItem }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseValue(
                results,
                caseItem.caseId,
                (caseItem) => caseItem.valueDriverInitial
              )}
              theme={CellValueTheme.Tertiary}
              dataTestId={`value-driver-additional-cell-${caseItem.caseId}`}
            />
          )}
        />
        <TableSectionValuesPerForecast
          title={l('_ExitDriverYoYGrowth', { driverName: valuationValueDriver.narrative })}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...enteredPercentageViewRules}
              value={getCalculatedProjectValueInPercent(
                getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) => year.valueDriverGrowth
                ) as Nullable<number>
              )}
              dataTestId={`exit-driver-growth-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
        <TableSectionValuesPerMultiple
          title={l('_ExitEV')}
          renderCell={({ caseItem, forecast, multipleId }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearMultipleValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                multipleId!,
                (multiple) => multiple.exitEv
              )}
              dataTestId={`exit-ev-cell-${caseItem.caseId}-${forecast.forecastId}-${multipleId}`}
            />
          )}
        />
        <TableSectionValuesPerForecast
          renderSpacer={false}
          title={l('_WeightedEV')}
          additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
          highlight
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseYearValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                (year) => year.weightedExitEv
              )}
              strong
              dataTestId={`weighted-ev-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
          renderAdditionalCell={({ caseItem }) => (
            <CellValue
              {...amountRules}
              value={getWaterfallCaseValue(
                results,
                caseItem.caseId,
                (caseItem) => caseItem.weightedExitEvInitial
              )}
              strong
              theme={CellValueTheme.Tertiary}
              dataTestId={`weighted-ev-additional-cell-${caseItem.caseId}`}
            />
          )}
        />
        <TableSectionValuesPerForecast
          title={l('_WeightedEVYoYGrowth')}
          renderCell={({ caseItem, forecast }) => (
            <CellValue
              {...enteredPercentageViewRules}
              value={getCalculatedProjectValueInPercent(
                getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) => year.weightedExitEvGrowth
                ) as Nullable<number>
              )}
              dataTestId={`weighted-ev-growth-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      </tbody>
    </>
  );
};
